import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Calendar,
  Building2, Target, ArrowUpRight, Download, Share2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Format currency
const formatCurrency = (value, compact = false) => {
  if (value === undefined || value === null) return '$0';
  if (compact) {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Mini chart component for sparklines
const MiniChart = ({ data, color, height = 40 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data.map(d => Math.abs(d)));
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full" style={{ height }}>
      <defs>
        <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${points} 100,${height}`}
        fill={`url(#gradient-${color})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Elegant Pro Forma Display Component
const ProFormaView = ({ document }) => {
  const { title, company_name, assumptions, projections, custom_labels, branding } = document;
  
  const primaryColor = branding?.primary_color || '#6366f1';
  
  // Calculate key metrics
  const year1 = projections[0] || {};
  const year5 = projections[4] || {};
  const year10 = projections[9] || {};
  
  const breakEvenYear = projections.findIndex(p => p.netIncome > 0) + 1;
  const totalRevenue = projections.reduce((sum, p) => sum + (p.grossRevenue || 0), 0);
  const totalProfit = projections.reduce((sum, p) => sum + (p.netIncome || 0), 0);
  
  const revenueData = projections.map(p => p.grossRevenue || 0);
  const profitData = projections.map(p => p.netIncome || 0);
  const customerData = projections.map(p => p.customers || 0);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={company_name} className="h-10 w-10 rounded-lg object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Building2 size={20} className="text-white" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white">{company_name || title}</h1>
                <p className="text-sm text-white/60">10-Year Financial Projections</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Eye size={14} />
              <span>{document.views || 0} views</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-indigo-500/20 to-indigo-600/5 rounded-2xl p-6 border border-indigo-500/20"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-indigo-300 text-sm font-medium">Year 10 Revenue</span>
              <TrendingUp size={16} className="text-indigo-400" />
            </div>
            <p className="text-3xl font-bold text-white">{formatCurrency(year10.grossRevenue, true)}</p>
            <div className="mt-2 h-10">
              <MiniChart data={revenueData} color="#818cf8" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 rounded-2xl p-6 border border-emerald-500/20"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-emerald-300 text-sm font-medium">Year 10 Net Income</span>
              {year10.netIncome >= 0 ? <TrendingUp size={16} className="text-emerald-400" /> : <TrendingDown size={16} className="text-red-400" />}
            </div>
            <p className={`text-3xl font-bold ${year10.netIncome >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatCurrency(year10.netIncome, true)}
            </p>
            <div className="mt-2 h-10">
              <MiniChart data={profitData} color={year10.netIncome >= 0 ? "#34d399" : "#f87171"} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-purple-500/20 to-purple-600/5 rounded-2xl p-6 border border-purple-500/20"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-purple-300 text-sm font-medium">{custom_labels?.customers || 'Customers'}</span>
              <Users size={16} className="text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-white">{(year10.customers || 0).toLocaleString()}</p>
            <div className="mt-2 h-10">
              <MiniChart data={customerData} color="#a78bfa" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-amber-500/20 to-amber-600/5 rounded-2xl p-6 border border-amber-500/20"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-amber-300 text-sm font-medium">Break-Even Year</span>
              <Target size={16} className="text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {breakEvenYear > 0 ? `Year ${breakEvenYear}` : 'N/A'}
            </p>
            <p className="text-xs text-amber-300/60 mt-2">First profitable year</p>
          </motion.div>
        </div>

        {/* Beautiful P&L Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-black/30 rounded-2xl border border-white/10 p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6">Revenue & Profitability</h3>
          
          {/* Revenue Bars */}
          <div className="space-y-6">
            <div>
              <p className="text-xs text-indigo-300 uppercase tracking-wider mb-3">Gross Revenue by Year</p>
              <div className="flex items-end gap-2 h-32">
                {projections.map((p, i) => {
                  const maxRev = Math.max(...projections.map(x => x.grossRevenue));
                  const height = (p.grossRevenue / maxRev) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ delay: 0.5 + i * 0.05, duration: 0.5 }}
                        className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-lg relative group cursor-pointer"
                      >
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 px-2 py-1 rounded text-xs text-white whitespace-nowrap z-10 shadow-xl">
                          {formatCurrency(p.grossRevenue, true)}
                        </div>
                      </motion.div>
                      <span className="text-[10px] text-white/50">Y{p.year}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Net Income Bars */}
            <div>
              <p className="text-xs text-emerald-300 uppercase tracking-wider mb-3">Net Income by Year</p>
              <div className="flex items-end gap-2 h-24">
                {projections.map((p, i) => {
                  const maxNet = Math.max(...projections.map(x => Math.abs(x.netIncome)));
                  const height = (Math.abs(p.netIncome) / maxNet) * 100;
                  const isPositive = p.netIncome >= 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ delay: 0.8 + i * 0.05, duration: 0.5 }}
                        className={`w-full rounded-t-lg relative group cursor-pointer ${
                          isPositive 
                            ? 'bg-gradient-to-t from-emerald-600 to-emerald-400' 
                            : 'bg-gradient-to-t from-red-600 to-red-400'
                        }`}
                      >
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 px-2 py-1 rounded text-xs text-white whitespace-nowrap z-10 shadow-xl">
                          {formatCurrency(p.netIncome, true)}
                        </div>
                      </motion.div>
                      <span className="text-[10px] text-white/50">Y{p.year}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Detailed Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-black/30 rounded-2xl border border-white/10 overflow-hidden"
        >
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white">Detailed Projections</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left py-3 px-4 text-white/70 font-medium sticky left-0 bg-slate-900/95">Metric</th>
                  {projections.map(p => (
                    <th key={p.year} className="text-right py-3 px-3 text-white/70 font-medium min-w-[90px]">
                      Year {p.year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                <tr className="hover:bg-white/5">
                  <td className="py-3 px-4 text-white/80 sticky left-0 bg-slate-900/95">{custom_labels?.customers || 'Customers'}</td>
                  {projections.map(p => (
                    <td key={p.year} className="text-right py-3 px-3 text-white/60 font-mono">
                      {(p.customers || 0).toLocaleString()}
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5 bg-indigo-500/10">
                  <td className="py-3 px-4 text-indigo-300 font-medium sticky left-0 bg-slate-900/95">{custom_labels?.revenue || 'Gross Revenue'}</td>
                  {projections.map(p => (
                    <td key={p.year} className="text-right py-3 px-3 text-indigo-300 font-mono">
                      {formatCurrency(p.grossRevenue, true)}
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="py-3 px-4 text-red-300/80 sticky left-0 bg-slate-900/95">{custom_labels?.cogs || 'Cost of Goods Sold'}</td>
                  {projections.map(p => (
                    <td key={p.year} className="text-right py-3 px-3 text-red-300/60 font-mono">
                      ({formatCurrency(p.cogs, true)})
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5 bg-emerald-500/10">
                  <td className="py-3 px-4 text-emerald-300 font-medium sticky left-0 bg-slate-900/95">Gross Profit</td>
                  {projections.map(p => (
                    <td key={p.year} className="text-right py-3 px-3 text-emerald-300 font-mono">
                      {formatCurrency(p.grossProfit, true)}
                      <span className="text-xs ml-1 text-emerald-400/50">({p.grossMargin}%)</span>
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="py-3 px-4 text-purple-300/80 sticky left-0 bg-slate-900/95">{custom_labels?.salaries || 'Team Costs'}</td>
                  {projections.map(p => (
                    <td key={p.year} className="text-right py-3 px-3 text-purple-300/60 font-mono">
                      ({formatCurrency(p.salaries, true)})
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="py-3 px-4 text-white/60 sticky left-0 bg-slate-900/95">{custom_labels?.opex || 'Operating Expenses'}</td>
                  {projections.map(p => (
                    <td key={p.year} className="text-right py-3 px-3 text-white/40 font-mono">
                      ({formatCurrency(p.opex, true)})
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5 bg-amber-500/10">
                  <td className="py-3 px-4 text-amber-300 font-medium sticky left-0 bg-slate-900/95">EBITDA</td>
                  {projections.map(p => (
                    <td key={p.year} className={`text-right py-3 px-3 font-mono ${p.ebitda >= 0 ? 'text-amber-300' : 'text-red-400'}`}>
                      {formatCurrency(p.ebitda, true)}
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5 bg-gradient-to-r from-emerald-500/20 to-emerald-500/10 font-bold">
                  <td className="py-4 px-4 text-white sticky left-0 bg-slate-900/95 text-base">Net Income</td>
                  {projections.map(p => (
                    <td key={p.year} className={`text-right py-4 px-3 font-mono text-base ${p.netIncome >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                      {formatCurrency(p.netIncome, true)}
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="py-3 px-4 text-white/60 sticky left-0 bg-slate-900/95">Team Size</td>
                  {projections.map(p => (
                    <td key={p.year} className="text-right py-3 px-3 text-white/40 font-mono">
                      {p.headcount}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Assumptions */}
        {assumptions && Object.keys(assumptions).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-black/30 rounded-2xl border border-white/10 p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Model Assumptions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {assumptions.customerGrowthRate && (
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-white/50 uppercase tracking-wider">Customer Growth</p>
                  <p className="text-xl font-bold text-white mt-1">{assumptions.customerGrowthRate}%</p>
                  <p className="text-xs text-white/40">per year</p>
                </div>
              )}
              {assumptions.averageRevenue && (
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-white/50 uppercase tracking-wider">Avg Revenue/Customer</p>
                  <p className="text-xl font-bold text-white mt-1">{formatCurrency(assumptions.averageRevenue)}</p>
                  <p className="text-xs text-white/40">per year</p>
                </div>
              )}
              {assumptions.initialCogs && (
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-white/50 uppercase tracking-wider">Initial COGS</p>
                  <p className="text-xl font-bold text-white mt-1">{assumptions.initialCogs}%</p>
                  <p className="text-xs text-white/40">of revenue</p>
                </div>
              )}
              {assumptions.avgSalary && (
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-white/50 uppercase tracking-wider">Avg Salary</p>
                  <p className="text-xl font-bold text-white mt-1">{formatCurrency(assumptions.avgSalary)}</p>
                  <p className="text-xs text-white/40">per person</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Footer */}
        <div className="text-center py-8 border-t border-white/10">
          <p className="text-white/30 text-sm">
            Generated with Bliss Abode • {new Date().toLocaleDateString()}
          </p>
        </div>
      </main>
    </div>
  );
};

// One-Pager / Business Plan View Component - Professional Document Display
const DocumentView = ({ document }) => {
  const { title, company_name, content, branding, metadata, document_type } = document;
  
  // Parse sections from content if it's a string with headers
  const parseContentSections = (text) => {
    if (!text) return [];
    
    // Split by section headers (lines in ALL CAPS or with === or ---)
    const lines = text.split('\n');
    const sections = [];
    let currentSection = { title: '', content: [] };
    
    for (const line of lines) {
      // Detect section headers - ALL CAPS lines or lines with === or ---
      const isHeader = /^[A-Z][A-Z\s\d:&\-()]+$/.test(line.trim()) && line.trim().length > 3;
      const isSeparator = /^[=\-]{3,}/.test(line.trim());
      
      if (isHeader && !isSeparator) {
        // Save previous section if it has content
        if (currentSection.title || currentSection.content.length > 0) {
          sections.push({
            title: currentSection.title,
            content: currentSection.content.join('\n').trim()
          });
        }
        currentSection = { title: line.trim(), content: [] };
      } else if (!isSeparator) {
        currentSection.content.push(line);
      }
    }
    
    // Don't forget the last section
    if (currentSection.title || currentSection.content.length > 0) {
      sections.push({
        title: currentSection.title,
        content: currentSection.content.join('\n').trim()
      });
    }
    
    return sections.filter(s => s.content.trim());
  };
  
  const sections = typeof content === 'string' ? parseContentSections(content) : (content?.sections || []);
  const wordCount = metadata?.word_count || (typeof content === 'string' ? content.split(' ').length : 0);
  
  // Get document type label
  const getDocumentTypeLabel = () => {
    switch (document_type) {
      case 'business_plan': return 'Business Plan';
      case 'expert_analysis': return 'Expert Analysis';
      case 'one_page_summary': return 'One-Page Summary';
      case 'executive_summary': return 'Executive Summary';
      case 'pitch_deck_outline': return 'Pitch Deck Outline';
      case 'business_model': return 'Business Model';
      default: return 'Business Document';
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Elegant Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-8 py-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-5">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={company_name} className="h-14 w-14 rounded-xl object-cover shadow-lg" />
              ) : (
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                  <Building2 size={26} className="text-white" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                  {company_name || title || 'Business Document'}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full">
                    {getDocumentTypeLabel()}
                  </span>
                  {wordCount > 0 && (
                    <span className="text-sm text-slate-500">
                      {wordCount.toLocaleString()} words
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Eye size={14} />
              <span>{document.views || 0} views</span>
            </div>
          </div>
        </div>
      </header>

      {/* Document Content */}
      <main className="max-w-4xl mx-auto px-8 py-12">
        {sections.length > 0 ? (
          <div className="space-y-10">
            {sections.map((section, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="scroll-mt-24"
                id={`section-${i}`}
              >
                {section.title && (
                  <h2 className="text-xl font-bold text-slate-800 mb-4 pb-3 border-b-2 border-indigo-100 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                      {i + 1}
                    </span>
                    {section.title}
                  </h2>
                )}
                <div className="text-slate-700 leading-relaxed whitespace-pre-wrap text-[15px] pl-11">
                  {section.content}
                </div>
              </motion.section>
            ))}
          </div>
        ) : (
          <div className="prose prose-slate prose-lg max-w-none">
            <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
              {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
            </div>
          </div>
        )}
      </main>

      {/* Elegant Footer */}
      <footer className="border-t border-slate-200 bg-white/50 mt-16">
        <div className="max-w-4xl mx-auto px-8 py-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">B</span>
            </div>
            <span className="text-sm font-medium text-slate-600">Bliss Abode Startup Studio</span>
          </div>
          <p className="text-slate-400 text-sm">
            Generated {metadata?.generated_at ? new Date(metadata.generated_at).toLocaleDateString() : new Date().toLocaleDateString()}
          </p>
          <p className="text-slate-300 text-xs mt-2">
            Confidential • Shared via secure link
          </p>
        </div>
      </footer>
    </div>
  );
};

// Keep the original OnePagerView for backwards compatibility
const OnePagerView = DocumentView;

// Main ShareView Component
const ShareView = () => {
  const { shareCode } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(`${API_URL}/api/share/${shareCode}`);
        if (!response.ok) {
          throw new Error('Document not found');
        }
        const data = await response.json();
        setDocument(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (shareCode) {
      fetchDocument();
    }
  }, [shareCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔒</span>
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Document Not Found</h1>
          <p className="text-white/60">This link may have expired or been revoked.</p>
        </div>
      </div>
    );
  }

  if (!document) return null;

  // Render based on document type
  switch (document.document_type) {
    case 'pro_forma':
      return <ProFormaView document={document} />;
    case 'one_pager':
    case 'one_page_summary':
    case 'business_plan':
    case 'executive_summary':
    case 'expert_analysis':
    case 'pitch_deck_outline':
    case 'business_model':
      return <DocumentView document={document} />;
    default:
      // Default to document view for text-based content
      if (typeof document.content === 'string') {
        return <DocumentView document={document} />;
      }
      return <ProFormaView document={document} />;
  }
};

export default ShareView;
